import React, { useCallback, useEffect, useReducer } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { GoogleTagManager as GTM } from '@vpr/tracking';

import LoadingPage from '@vimeo/core/components/LoadingPage';
import { ResourceNotFoundError } from '@vimeo/core/libs/errors';
import { Viewer } from '@vimeo/viewer';

import BrandedLogin from './BrandedLogin';
import ShowcaseErrorScreen from './components/ShowcaseErrorScreen';
import { useShowcaseEmbed } from './hooks/useShowcaseEmbed';
import PasswordPrompt from './PasswordPrompt';
import { Showcase } from './Showcase';
import { AlbumThemes } from './types';
import UserNotAllowed from './UserNotAllowed';
import { LAYOUT_PLAYLIST } from './utils/constants';
import { convertColorForDisplay, parseQuery } from './utils/helpers';

if (typeof window !== 'undefined') {
  GTM.init((window as any)._gtm || []);
}

enum ErrorCode {
  passwordPrivacy = 0,
  incorrectPassword = 1,
  teamPrivacy = 2,
  teamPrivacyLoggedInUser = 3,
  embedOnly = 4,
  unknownReason = 5,
  unlisted = 6,
  showcaseNotFound = 7,
}

type AlbumMetadata = {
  name: string;
  url: string;
  creator: number;
  brand_color: string | null;
  has_sso: boolean;
  custom_logo: string | null;
  theme: AlbumThemes;
  id: number;
  domain: string;
  sso_connections_count: number;
  layout: string | null;
};

type State = {
  isLoading: boolean;
  allowAccess: boolean;
  errorCode: ErrorCode | null;
  hashedPass: string;
  metadata: AlbumMetadata;
};

type AuthResponse = {
  error_code: number | null;
  metadata: AlbumMetadata;
  hashed_pass: string;
};

type Action =
  | { type: 'AUTH_FAILURE'; payload: AuthResponse }
  | { type: 'AUTH_SUCCESS'; payload: AuthResponse }
  | { type: 'PASSWORD_SUCCESS'; payload: string };

interface Params {
  albumId: string;
  albumHash?: string;
}

interface Props extends RouteComponentProps<Params> {
  viewer: Viewer;
}

function Authorization({ viewer, match, location, history }: Props) {
  const { getEmbedQueryParam } = useShowcaseEmbed();

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case 'AUTH_FAILURE':
        return {
          ...state,
          isLoading: false,
          allowAccess:
            action?.payload?.metadata?.layout === LAYOUT_PLAYLIST &&
            action?.payload?.error_code === ErrorCode.passwordPrivacy,
          metadata: action.payload.metadata,
          errorCode: action.payload.error_code,
        };
      case 'AUTH_SUCCESS':
        return {
          ...state,
          isLoading: false,
          allowAccess: true,
          hashedPass: action.payload.hashed_pass,
          metadata: action.payload.metadata,
        };
      case 'PASSWORD_SUCCESS':
        return {
          ...state,
          allowAccess: true,
          hashedPass: action.payload,
        };
      default:
        return state;
    }
  }

  const [{ isLoading, allowAccess, errorCode, hashedPass, metadata }, dispatch] = useReducer(
    reducer,
    {
      isLoading: true,
      allowAccess: false,
      errorCode: null,
      hashedPass: '',
      metadata: {
        name: '',
        url: '',
        creator: 0,
        brand_color: null,
        has_sso: false,
        custom_logo: null,
        id: 0,
        theme: 'standard',
        domain: '',
        sso_connections_count: 0,
        layout: '',
      },
    },
  );

  const albumId = match ? match.params.albumId : '';
  const albumHash = parseQuery(location.search).sh || '';

  const authRequest = useCallback(async () => {
    try {
      const suffix = albumHash ? `?sh=${albumHash}` : '';
      const authUrl = albumId
        ? `/showcase/${albumId}/auth${suffix}${getEmbedQueryParam('?')}`
        : '/auth';
      const response = await fetch(authUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      return await response.json();
    } catch (error) {
      return {
        error_code: ErrorCode.unknownReason,
      };
    }
  }, [albumHash, albumId, getEmbedQueryParam]);

  useEffect(() => {
    const authorization = async () => {
      const authStatus: AuthResponse = await authRequest();
      if (authStatus.error_code !== null) {
        dispatch({ type: 'AUTH_FAILURE', payload: authStatus });
      } else {
        dispatch({ type: 'AUTH_SUCCESS', payload: authStatus });
      }
    };
    authorization();
  }, []);

  if (!match || !viewer) {
    return null;
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!allowAccess) {
    switch (errorCode) {
      case ErrorCode.passwordPrivacy:
        return (
          <PasswordPrompt
            albumId={metadata.id}
            xsrft={viewer.xsrft}
            onSuccess={(hash: string) => {
              dispatch({ type: 'PASSWORD_SUCCESS', payload: hash });
            }}
          />
        );
      case ErrorCode.teamPrivacy:
        return (
          <BrandedLogin
            name={metadata.name}
            userId={metadata.creator}
            url={metadata.url}
            brandColor={convertColorForDisplay(metadata.brand_color)}
            ssoEnabled={metadata.has_sso}
            customLogo={metadata.custom_logo}
            theme={metadata.theme}
            domain={metadata.domain}
            id={metadata.id}
            ssoConnectionsCount={metadata.sso_connections_count}
          />
        );
      case ErrorCode.teamPrivacyLoggedInUser:
        return (
          <UserNotAllowed
            name={metadata.name}
            brandColor={convertColorForDisplay(metadata.brand_color)}
            customLogo={metadata.custom_logo}
            theme={metadata.theme}
          />
        );
      case ErrorCode.showcaseNotFound:
        throw new ResourceNotFoundError();
      case ErrorCode.embedOnly:
      case ErrorCode.unknownReason:
      case ErrorCode.unlisted:
      default:
        return <ShowcaseErrorScreen />;
    }
  }

  return (
    <Showcase
      viewer={viewer}
      match={match}
      location={location}
      history={history}
      hashedPass={hashedPass}
      id={metadata.id}
      albumHash={albumHash}
    />
  );
}

export default Authorization;
